import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import DialogDefault from '../../components/DialogDefault/DialogDefault';

const STATUS_ORDER = {
  EM_CRIACAO: 1,
  AGUARDANDO_INSPECAO: 2,
  EM_INSPECAO: 3,
  INSPECAO_FINALIZADA: 4,
  AGUARDANDO_DIGITACAO: 5,
  EM_DIGITACAO: 6,
  DIGITACAO_FINALIZADA: 7,
  EXECUTADA_GDIS: 8,
};

const useStyles = makeStyles(theme => ({
  intro: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  tableContainer: {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 400,
  },
  tableHeader: {
    '& th': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
      whiteSpace: 'nowrap',
    },
  },
  similarityMain: {
    fontWeight: 600,
  },
  similarityDetail: {
    display: 'block',
    color: theme.palette.text.secondary,
  },
  linkCell: {
    whiteSpace: 'nowrap',
  },
  openLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const SimilarityModal = ({ open, results, onCancel, onProceed, informationalOnly }) => {
  const classes = useStyles();

  const sorted = [...results].sort((a, b) => {
    if (b.similarityPercentage !== a.similarityPercentage) {
      return b.similarityPercentage - a.similarityPercentage;
    }
    return (STATUS_ORDER[a.status] || 99) - (STATUS_ORDER[b.status] || 99);
  });

  return (
    <DialogDefault
      open={open}
      title="NSIRs Similares Encontradas"
      handlerCancel={onCancel}
      handlerConfirm={onProceed}
      cancelMessage="Cancelar"
      confirmMessage="Prosseguir"
      fullWidth
      maxWidth="xl"
      disableBackdropClick
      readOnly={informationalOnly}
    >
      <Typography variant="body2" className={classes.intro}>
        Foram encontradas NSIRs similares à que está sendo criada. Verifique as informações abaixo antes de continuar.
      </Typography>
      <div className={classes.tableContainer}>
        <Table size="small" stickyHeader>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>NSIR</TableCell>
              <TableCell>Equipamento</TableCell>
              <TableCell>Tipo de Inspeção</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Inspetor</TableCell>
              <TableCell>Similaridade</TableCell>
              <TableCell>Razão</TableCell>
              <TableCell>Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.codigo}</TableCell>
                <TableCell>{row.equipamento}</TableCell>
                <TableCell>
                  <Typography className={classes.similarityMain}>
                    {row.tipoServico}
                  </Typography>
                  {row.subtipo && <Typography variant="caption" className={classes.similarityDetail}>
                    {row.subtipo}
                  </Typography>}
                </TableCell>
                <TableCell>
                  <Typography className={classes.similarityMain}>
                    {row.statusValue}
                  </Typography>
                  {row.criadoPor && <Typography variant="caption" className={classes.similarityDetail}>
                    Criado por: {row.criadoPor}
                  </Typography>}
                  {row.criadoEm && <Typography variant="caption" className={classes.similarityDetail}>
                    Criado em: {row.criadoEm}
                  </Typography>}
                  {row.dataConclusao && <Typography variant="caption" className={classes.similarityDetail}>
                    Executada GDIS: {row.dataConclusao}
                  </Typography>}
                </TableCell>
                <TableCell>{row.inspetor}</TableCell>
                <TableCell>
                  <Typography className={classes.similarityMain}>
                    {`${row.similarityPercentage}%`}
                  </Typography>
                  <Typography variant="caption" className={classes.similarityDetail}>
                    {`Equipamento: ${row.equipamentoSimilarity}%`}
                  </Typography>
                  <Typography variant="caption" className={classes.similarityDetail}>
                    {`Código: ${row.codigoSimilarity}%`}
                  </Typography>
                </TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell className={classes.linkCell}>
                  <Link
                    to={`/nsir/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.openLink}
                  >
                    Abrir
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DialogDefault>
  );
};

SimilarityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  results: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onProceed: PropTypes.func.isRequired,
  informationalOnly: PropTypes.bool,
};

SimilarityModal.defaultProps = {
  informationalOnly: false,
};

export default SimilarityModal;
